// Read individual env values off the LIVE gateway container app.
//
// Why this exists: deploy/main.bicep declares `voiceInternalSecret` with a
// `newGuid()` default, which re-evaluates on every deployment. Any Bicep re-run
// that does not pass the current value therefore mints a new shared secret and
// rolls BOTH the gateway and the voice relay. Re-run paths read the live value
// back and pass it through — read the deployed state, never reconstruct it.
//
// SECRET HYGIENE: the returned value is used in memory only. It is never logged,
// never written to a file, and never stored in the system/infra-params row.
import { runAz } from "./az.js";

/**
 * Read one env var from the gateway container app's first container.
 * Returns "" when the app has no such var, or when the read fails — callers
 * treat "" as "omit the param", which preserves first-deploy behaviour.
 */
export async function readLiveGatewayEnvVar(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
  name: string;
}): Promise<string> {
  const args = ["containerapp", "show", "-n", opts.gatewayName, "-g", opts.resourceGroup];
  if (opts.subscriptionId) args.push("--subscription", opts.subscriptionId);
  args.push("--query", `properties.template.containers[0].env[?name=='${opts.name}'].value | [0]`, "-o", "tsv");
  let raw: string;
  try {
    raw = await runAz(args);
  } catch {
    return "";
  }
  const v = raw.trim();
  // `az … -o tsv` prints the literal `None` for a null scalar on some versions.
  return v === "None" ? "" : v;
}

/** The voice relay ↔ gateway shared secret, read off live state. Never log the result. */
export function readLiveVoiceInternalSecret(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
}): Promise<string> {
  return readLiveGatewayEnvVar({ ...opts, name: "M8T_INTERNAL_SECRET" });
}
